import React, { useState, useMemo } from 'react';
import { PDVMarketResearch } from '@/api/business/marketResearch/type';
import { useGeolocationGuard } from '@/hooks/useGeolocationGuard';
import LocationPermissionModal from '@/components/LocationPermissionModal';
import {
  Search,
  MapPin,
  Store,
  CheckCircle2,
  Clock,
  XCircle,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface SellerClientListProps {
  clients: PDVMarketResearch[];
  isLoading?: boolean;
  onSelectClient: (
    client: PDVMarketResearch,
    coords: { latitude: number; longitude: number },
  ) => void;
}

export const SellerClientList: React.FC<SellerClientListProps> = ({
  clients,
  isLoading = false,
  onSelectClient,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    'ALL' | 'Pendente' | 'Concluída' | 'Não Realizada'
  >('ALL');
  const [pendingClient, setPendingClient] = useState<PDVMarketResearch | null>(
    null,
  );

  const { isChecking, errorMessage, requestLocation } = useGeolocationGuard();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filter clients
  const filteredClients = useMemo(() => {
    return clients?.filter((client) => {
      // Text search
      if (searchTerm.trim() !== '') {
        const term = searchTerm.toLowerCase();
        const nameMatch =
          client?.DS_NOME_FANTASIA?.toLowerCase()?.includes(term);
        const cityMatch = client?.DS_CIDADE?.toLowerCase()?.includes(term);
        if (!nameMatch && !cityMatch) return false;
      }

      // Status filter
      const clientStatus = client?.DD_STATUS_PESQUISA || 'Pendente';
      if (statusFilter !== 'ALL' && clientStatus !== statusFilter) {
        return false;
      }

      return true;
    });
  }, [clients, searchTerm, statusFilter]);

  // Handle client selection with location check (RF001, RF002, RF006, RN001, RN006)
  const handleClientClick = async (client: PDVMarketResearch) => {
    if (isChecking) return;
    setPendingClient(client);
    const loc = await requestLocation();
    if (loc) {
      onSelectClient(client, loc);
      setPendingClient(null);
    } else {
      setIsModalOpen(true);
    }
  };

  const handleRetryLocation = async () => {
    const loc = await requestLocation();
    if (loc && pendingClient) {
      setIsModalOpen(false);
      onSelectClient(pendingClient, loc);
      setPendingClient(null);
    }
  };

  const renderStatusBadge = (status?: string) => {
    switch (status) {
      case 'Concluída':
        return (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-green-700 bg-green-100 border border-green-200 px-2.5 py-1 rounded-full">
            <CheckCircle2 size={13} />
            Concluída
          </span>
        );
      case 'Não Realizada':
        return (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-700 bg-red-100 border border-red-200 px-2.5 py-1 rounded-full">
            <XCircle size={13} />
            Não Realizada
          </span>
        );
      case 'Pendente':
      default:
        return (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-100 border border-amber-200 px-2.5 py-1 rounded-full">
            <Clock size={13} />
            Pendente
          </span>
        );
    }
  };

  return (
    <div className="space-y-4">
      {/* Search & Filter Header */}
      <div className="p-4 space-y-3 bg-white border border-gray-200 shadow-sm rounded-2xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
            <Store size={18} className="text-primary" />
            <span>
              Locais de pesquisa {!isLoading && `(${filteredClients.length})`}
            </span>
          </div>
          <span className="text-xs text-gray-500">
            {isChecking
              ? 'Obtendo localização...'
              : 'Selecione para iniciar pesquisa'}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {/* Search Input */}
          <div className="relative">
            <Search
              size={16}
              className="absolute text-gray-400 -translate-y-1/2 left-3 top-1/2"
            />
            <Input
              placeholder="Buscar por cliente, código ou cidade..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={isLoading || isChecking}
              className="h-10 text-xs bg-white pl-9"
            />
          </div>

          {/* Status Filter Tabs */}
          <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
            <Button
              variant={statusFilter === 'ALL' ? 'default' : 'ghost'}
              size="sm"
              disabled={isLoading || isChecking}
              className="flex-1 text-[11px] h-8 font-medium"
              onClick={() => setStatusFilter('ALL')}
            >
              Todos
            </Button>
            <Button
              variant={statusFilter === 'Pendente' ? 'default' : 'ghost'}
              size="sm"
              disabled={isLoading || isChecking}
              className="flex-1 text-[11px] h-8 font-medium"
              onClick={() => setStatusFilter('Pendente')}
            >
              Pendentes
            </Button>
            <Button
              variant={statusFilter === 'Concluída' ? 'default' : 'ghost'}
              size="sm"
              disabled={isLoading || isChecking}
              className={cn(
                'flex-1 text-[11px] h-8 font-medium text-green-700',
                statusFilter === 'Concluída' && 'text-white',
              )}
              onClick={() => setStatusFilter('Concluída')}
            >
              Concluídas
            </Button>
            {/* <Button
              variant={statusFilter === 'Não Realizada' ? 'default' : 'ghost'}
              size="sm"
              disabled={isLoading || isChecking}
              className="flex-1 text-[11px] h-8 font-medium text-red-700"
              onClick={() => setStatusFilter('Não Realizada')}
            >
              Não Realiz.
            </Button> */}
          </div>
        </div>
      </div>

      {/* Client List */}
      {isLoading ? (
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-2 py-2 text-xs font-medium text-gray-500">
            <Loader2 size={16} className="animate-spin text-primary" />
            <span>Carregando clientes do vendedor...</span>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="p-4 space-y-3 bg-white border border-gray-200 shadow-sm rounded-2xl"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <Skeleton className="w-10 h-10 rounded-xl" />
                    <div className="space-y-1.5">
                      <Skeleton className="h-4 w-36" />
                      <Skeleton className="w-20 h-3" />
                    </div>
                  </div>
                  <Skeleton className="w-20 h-6 rounded-full" />
                </div>
                <div className="pt-1 border-t border-gray-100 space-y-1.5">
                  <Skeleton className="w-48 h-3" />
                  <Skeleton className="h-3 w-28" />
                </div>
                <div className="flex justify-end pt-2">
                  <Skeleton className="w-16 h-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="py-12 space-y-2 text-xs text-center text-gray-400 bg-white border border-gray-200 rounded-2xl">
          <Store size={32} className="mx-auto text-gray-300" />
          <p className="font-semibold text-gray-600">
            Nenhum cliente encontrado
          </p>
          <p className="text-gray-400">
            Tente ajustar os filtros digitados acima.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {filteredClients.map((client) => {
            const isClientSelected =
              pendingClient?.SK_CLIENTE === client.SK_CLIENTE;
            const isClientLoading = isChecking && isClientSelected;
            const isAnyOtherLoading = isChecking && !isClientSelected;

            return (
              <div
                key={client.SK_CLIENTE}
                onClick={() => handleClientClick(client)}
                className={`bg-white border rounded-2xl p-4 shadow-sm transition-all flex flex-col justify-between space-y-3 relative overflow-hidden ${
                  isClientLoading
                    ? 'border-primary ring-2 ring-primary/20 bg-primary/[0.02] shadow-md cursor-wait'
                    : isAnyOtherLoading
                      ? 'opacity-50 border-gray-200 cursor-not-allowed'
                      : 'border-gray-200 hover:border-primary hover:shadow-md cursor-pointer group'
                }`}
              >
                {/* Progress bar on active loading card */}
                {isClientLoading && (
                  <div className="absolute top-0 left-0 right-0 h-1 overflow-hidden bg-primary/20">
                    <div className="w-full h-full bg-primary animate-pulse" />
                  </div>
                )}

                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                        isClientLoading
                          ? 'bg-primary text-white animate-pulse'
                          : 'bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white'
                      }`}
                    >
                      {isClientLoading ? (
                        <Loader2 size={20} className="animate-spin" />
                      ) : (
                        <Store size={20} />
                      )}
                    </div>
                    <div>
                      <h4
                        className={`text-sm font-bold text-gray-900 leading-tight transition-colors ${
                          !isAnyOtherLoading ? 'group-hover:text-primary' : ''
                        }`}
                      >
                        {client.DS_NOME_FANTASIA}
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Código:{' '}
                        <span className="font-medium text-gray-700">
                          {client.NK_CLIENTE}
                        </span>
                      </p>
                    </div>
                  </div>

                  {renderStatusBadge(client.DD_STATUS_PESQUISA)}
                </div>

                <div className="pt-1 space-y-1 text-xs text-gray-500 border-t border-gray-100">
                  <p className="flex items-center gap-1 truncate">
                    <MapPin size={13} className="text-gray-400 shrink-0" />
                    {client.DS_ENDERECO
                      ? `${client.DS_ENDERECO}, ${client.DD_NUMERO || 'S/N'}`
                      : 'Endereço não informado'}
                    {client.DS_CIDADE
                      ? ` - ${client.DS_CIDADE}/${client.DD_UF}`
                      : ''}
                  </p>
                  {client.NM_FUNCIONARIO && (
                    <p className="text-[11px] text-gray-400">
                      Vendedor:{' '}
                      <span className="text-gray-600">
                        {client.NM_FUNCIONARIO}
                      </span>
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-end pt-2 text-xs font-semibold text-primary">
                  {isClientLoading ? (
                    <div className="flex items-center gap-1.5 text-primary animate-pulse font-bold">
                      <Loader2 size={15} className="animate-spin" />
                      <span>Obtendo localização...</span>
                    </div>
                  ) : (
                    <div className="flex items-center transition-transform group-hover:translate-x-1">
                      <span>Acessar</span>
                      <ChevronRight size={16} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Geolocation Blocking Modal */}
      <LocationPermissionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setPendingClient(null);
        }}
        onRetry={handleRetryLocation}
        isChecking={isChecking}
        errorMessage={errorMessage}
      />
    </div>
  );
};

export default SellerClientList;
